"use client";

import React, { useMemo } from "react";
import { BaseChart } from "./base-chart";
import { chartTooltip, chartAnimation, chartColors, formatChartValue } from "./chart-utils";
import { resolveChartTokens } from "@/src/utils/chart-theme";

export interface TreemapNode {
  name: string;
  value: number;
  children?: TreemapNode[];
  color?: string;
}

export interface TreemapChartProps {
  data: TreemapNode[];
  title?: string;
  subtitle?: string;
  height?: string | number;
  className?: string;
  showBreadcrumb?: boolean;
  showLabels?: boolean;
  levels?: number;
  loading?: boolean;
  empty?: boolean;
  onNodeClick?: (params: any) => void;
  formatValue?: (value: number) => string;
}

export function TreemapChart({
  data,
  title,
  subtitle,
  height = 400,
  className,
  showBreadcrumb = true,
  showLabels = true,
  levels = 2,
  loading = false,
  empty = false,
  onNodeClick,
  formatValue = (v) => formatChartValue(v, 0),
}: TreemapChartProps) {
  const option = useMemo(() => {
    const tokens = resolveChartTokens();
    const colors = chartColors();
    const total = data.reduce((sum, n) => sum + n.value, 0);

    const coloredData = data.map((node, i) => ({
      ...node,
      itemStyle: { color: node.color || colors[i % colors.length] },
    }));

    const levelConfig = Array.from({ length: levels }, (_, i) => ({
      itemStyle: {
        borderColor: tokens.backgroundColor,
        borderWidth: i === 0 ? 3 : i === 1 ? 1 : 0,
        gapWidth: i === 0 ? 3 : i === 1 ? 1 : 0,
      },
      ...(i > 0 ? { colorSaturation: [0.35, 0.7] } : {}),
    }));

    return {
      ...(title ? { title: { text: title, subtext: subtitle || "", left: 0, textStyle: { fontSize: 16, fontWeight: 600, fontFamily: tokens.fontFamily, color: tokens.textColor }, subtextStyle: { fontSize: 12, fontFamily: tokens.fontFamily, color: tokens.textColorSecondary } } } : {}),
      ...chartTooltip({
        trigger: "item",
        formatter: (params: any) => {
          const pct = total > 0 ? ((params.value / total) * 100).toFixed(1) : "0";
          return `${params.name}<br/>${formatValue(params.value)} (${pct}%)`;
        },
      }),
      ...chartAnimation({ duration: 600 }),
      series: [{
        type: "treemap" as const,
        roam: false,
        nodeClick: "zoomToNode",
        width: "100%",
        height: "100%",
        top: title ? 48 : 8,
        breadcrumb: {
          show: showBreadcrumb,
          bottom: 4,
          itemStyle: { color: colors[0], textStyle: { color: "#fff", fontSize: 12, fontFamily: tokens.fontFamily } },
        },
        label: {
          show: showLabels,
          formatter: "{b}",
          fontSize: 12,
          fontFamily: tokens.fontFamily,
          color: "#fff",
          textShadowBlur: 2,
          textShadowColor: "rgba(0,0,0,0.3)",
        },
        upperLabel: { show: true, height: 24, fontSize: 11, fontFamily: tokens.fontFamily, color: "#fff" },
        levels: levelConfig,
        data: coloredData,
        animationDuration: 600,
        animationEasing: "cubicInOut",
      }],
    };
  }, [data, title, subtitle, showBreadcrumb, showLabels, levels, formatValue]);

  return (
    <BaseChart
      option={option}
      height={height}
      className={className}
      loading={loading}
      empty={empty || data.length === 0}
      onEvents={onNodeClick ? { click: onNodeClick } : undefined}
    />
  );
}

export default TreemapChart;
